import { useTranslation } from 'react-i18next';
import Logo from '@/components/feature/Logo';
import { socialLinks } from '@/mocks/social';

export default function Footer() {
  const { t } = useTranslation();

  const navItems = [
    { key: 'nav.services', href: '#services' },
    { key: 'nav.cases', href: '#cases' },
    { key: 'nav.about', href: '#about' },
    { key: 'nav.contact', href: '#contact' },
  ];

  return (
    <footer className="w-full bg-background-50 pb-6 pt-16 px-4 md:px-6">
      <div className="max-w-7xl mx-auto bg-foreground-950 rounded-3xl p-8 md:p-14 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-500/10 rounded-full blur-[100px]" />

        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-background-50/10">
          {/* Brand & subscribe */}
          <div className="lg:col-span-2">
            <Logo variant="light" />
            <p className="mt-6 text-sm text-background-200/80 leading-relaxed max-w-sm">
              {t('footer.brandDesc')}
            </p>

            <div className="mt-8">
              <div className="text-xs font-mono-tech tracking-widest uppercase text-background-100/60 mb-3">
                {t('footer.newsletter')}
              </div>
              <p className="text-xs text-background-200/70 mb-4 max-w-xs leading-relaxed">
                {t('footer.newsletter.desc')}
              </p>
              <form
                data-readdy-form
                id="af-newsletter-form"
                className="flex items-center gap-2 max-w-sm"
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  const honeypot = (formData.get('company_alt') as string) || '';
                  if (honeypot.trim() !== '') {
                    (e.currentTarget as HTMLFormElement).reset();
                    return;
                  }
                  // Simple UI feedback for footer newsletter — full submit could be wired similarly later
                  (e.currentTarget as HTMLFormElement).reset();
                }}
              >
                <div className="af-extra-field" aria-hidden="true">
                  <label>
                    Company
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </label>
                </div>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder={t('footer.newsletter.placeholder')}
                  className="flex-1 px-4 py-3 text-sm bg-transparent border-b border-background-50/30 text-background-50 placeholder:text-background-100/50 focus:outline-none focus:border-primary-500"
                />
                <button
                  type="submit"
                  className="w-11 h-11 rounded-full bg-primary-500 hover:bg-primary-400 text-foreground-950 flex items-center justify-center cursor-pointer transition-colors"
                  aria-label={t('footer.newsletter.button')}
                >
                  <i className="ri-arrow-right-line text-lg w-5 h-5 flex items-center justify-center" />
                </button>
              </form>
            </div>
          </div>

          {/* Nav */}
          <div>
            <div className="text-xs font-mono-tech tracking-widest uppercase text-background-100/60 mb-6">
              {t('footer.nav.title')}
            </div>
            <ul className="space-y-3">
              {navItems.map((item) => (
                <li key={item.key}>
                  <a
                    href={item.href}
                    className="text-sm text-background-100 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t(item.key)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="text-xs font-mono-tech tracking-widest uppercase text-background-100/60 mb-6">
              {t('footer.contact.title')}
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={`mailto:${t('footer.email')}`} className="text-background-100 hover:text-primary-500 transition-colors font-mono-tech text-xs tracking-wide whitespace-nowrap cursor-pointer">
                  {t('footer.email')}
                </a>
              </li>
              <li>
                <a href="tel:+85231500000" className="text-background-100 hover:text-primary-500 transition-colors font-mono-tech text-xs tracking-wide whitespace-nowrap cursor-pointer">
                  {t('footer.phone')}
                </a>
              </li>
              <li className="pt-4">
                <div className="text-xs font-mono-tech tracking-widest uppercase text-background-100/60 mb-2">
                  {t('footer.address.title')}
                </div>
                <p className="text-xs text-background-200/80 leading-relaxed">
                  {t('footer.address.value')}
                </p>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="relative z-10 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs font-mono-tech tracking-widest uppercase text-background-100/60">
              {t('footer.social.title')}
            </span>
            {socialLinks.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="nofollow noopener noreferrer"
                aria-label={s.name}
                className="w-9 h-9 rounded-full border border-background-50/20 text-background-100 hover:bg-primary-500 hover:text-foreground-950 hover:border-primary-500 flex items-center justify-center transition-all cursor-pointer"
              >
                <i className={`${s.icon} text-base w-4 h-4 flex items-center justify-center`} />
              </a>
            ))}
          </div>

          <div className="text-xs text-background-100/60 font-mono-tech tracking-wide">
            {t('footer.copyright')}
          </div>

          <div className="flex items-center gap-5">
            <a href="#" className="text-xs text-background-100/70 hover:text-primary-500 underline underline-offset-4 whitespace-nowrap cursor-pointer">
              {t('footer.policy.privacy')}
            </a>
            <a href="#" className="text-xs text-background-100/70 hover:text-primary-500 underline underline-offset-4 whitespace-nowrap cursor-pointer">
              {t('footer.policy.terms')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}